import { useRef, useState } from 'react';
import useEditorialMotion from '@/hooks/useEditorialMotion';
import type { FormEvent, ReactNode } from 'react';
import { budgets, enquiryRecipient, occasions } from '@/mocks/editorial';

type Fields = { name: string; email: string; occasion: string; date: string; budget: string; idea: string };
const empty: Fields = { name: '', email: '', occasion: '', date: '', budget: '', idea: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputCls = 'w-full box-border bg-transparent border-0 border-b border-black/20 focus:border-black hover:border-black/40 aria-[invalid=true]:border-[#9a3b2c] focus:outline-none py-2.5 text-[15px] transition-colors duration-300 rounded-none';

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <div className="group min-w-0">
      <label htmlFor={id} className="block text-[11px] tracking-wide text-black/50 group-focus-within:text-black transition-colors duration-200">{label}</label>
      {children}
      {error && <p id={`${id}-err`} className="ff-err mt-1.5 ff-serif italic text-[13px] text-[#9a3b2c]">{error}</p>}
    </div>
  );
}

export default function Enquiry() {
  const ref = useRef<HTMLElement>(null);
  useEditorialMotion(ref);
  const [f, setF] = useState<Fields>(empty);
  const [errors, setErrors] = useState<Partial<Fields>>({});
  const [summary, setSummary] = useState('');
  const [copied, setCopied] = useState('');

  const set = (k: keyof Fields) => (e: { target: { value: string } }) => {
    setF((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((v) => ({ ...v, [k]: undefined }));
  };

  const validate = () => {
    const e: Partial<Fields> = {};
    if (!f.name.trim()) e.name = 'Please add your name.';
    if (!f.email.trim()) e.email = 'Please add your email.';
    else if (!EMAIL_RE.test(f.email.trim())) e.email = 'This email doesn’t look quite right.';
    if (!f.idea.trim()) e.idea = 'Tell us a little about your idea.';
    else if (f.idea.length > 500) e.idea = 'Please keep it under 500 characters.';
    return e;
  };

  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    setCopied('');
    if (Object.keys(e).length) {
      document.getElementById(`enq-${Object.keys(e)[0]}`)?.focus();
      return;
    }
    setSummary([
      'Bespoke flower enquiry — Fleur & Form',
      `Name: ${f.name.trim()}`,
      `Email: ${f.email.trim()}`,
      `Occasion: ${f.occasion || 'Not specified'}`,
      `Preferred date: ${f.date || 'Flexible'}`,
      `Budget: ${f.budget || 'Not specified'}`,
      '',
      'Idea:',
      f.idea.trim(),
    ].join('\n'));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied('Copied to clipboard.');
    } catch {
      setCopied('Couldn’t copy automatically — select the text above instead.');
    }
  };

  const mailto = enquiryRecipient && EMAIL_RE.test(enquiryRecipient)
    ? `mailto:${enquiryRecipient}?subject=${encodeURIComponent('Bespoke flower enquiry')}&body=${encodeURIComponent(summary)}`
    : '';

  const err = (k: keyof Fields) => (errors[k] ? { 'aria-invalid': true, 'aria-describedby': `enq-${k}-err` } : {});

  return (
    <section ref={ref} id="bespoke" className="bg-[#FAFAF8] px-4 md:px-10 py-20 md:py-36 scroll-mt-4">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-14 lg:gap-24">
        <div className="min-w-0">
          <p data-m-fade className="text-[11px] md:text-[12px] tracking-wide text-black/50 mb-6 md:mb-8">07 / BESPOKE FLOWERS</p>
          <h2 data-m-head className="font-normal tracking-[-0.045em] leading-[0.95] text-[40px] sm:text-[56px] lg:text-[80px]">Something just for you.</h2>
          <p data-m-fade data-m-at="0.15" className="ff-serif italic text-[18px] md:text-[20px] text-black/60 mt-6">Tell us what you have in mind.</p>
          <p className="text-[14px] md:text-[15px] leading-relaxed text-black/70 mt-6 max-w-[420px]">
            From a single table piece to flowers for a whole day, every bespoke composition begins with a short note about the moment.
          </p>
        </div>

        <div className="min-w-0" data-m-in="up" data-m-dist="24" data-m-dur="0.8" data-m-at="0.1">
          {!summary ? (
            <form id="bespoke-enquiry" noValidate onSubmit={onSubmit} className="ff-soft-in flex flex-col gap-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <Field id="enq-name" label="Name *" error={errors.name}>
                  <input id="enq-name" name="name" autoComplete="name" value={f.name} onChange={set('name')} className={inputCls} {...err('name')} />
                </Field>
                <Field id="enq-email" label="Email *" error={errors.email}>
                  <input id="enq-email" name="email" type="email" autoComplete="email" value={f.email} onChange={set('email')} className={inputCls} {...err('email')} />
                </Field>
                <Field id="enq-occasion" label="Occasion">
                  <select id="enq-occasion" name="occasion" value={f.occasion} onChange={set('occasion')} className={`${inputCls} cursor-pointer`}>
                    <option value="">Select</option>
                    {occasions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field id="enq-date" label="Preferred date (optional)">
                  <input id="enq-date" name="date" type="date" value={f.date} onChange={set('date')} className={`${inputCls} cursor-pointer`} />
                </Field>
              </div>
              <Field id="enq-budget" label="Budget range">
                <select id="enq-budget" name="budget" value={f.budget} onChange={set('budget')} className={`${inputCls} cursor-pointer`}>
                  <option value="">Select</option>
                  {budgets.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field id="enq-idea" label="Your idea *" error={errors.idea}>
                <textarea id="enq-idea" name="idea" rows={4} maxLength={500} value={f.idea} onChange={set('idea')} className={`${inputCls} resize-none`} {...err('idea')} />
                <p className="mt-1 text-right text-[11px] text-black/35 tabular-nums">{f.idea.length}/500</p>
              </Field>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                <button type="submit" className="self-start bg-[#111111] text-white text-[13px] px-6 py-3 rounded-md whitespace-nowrap cursor-pointer hover:bg-black/80 transition-colors">Prepare enquiry</button>
                <p className="ff-serif italic text-[13px] text-black/45">Nothing is sent from this page — you’ll get a summary to copy.</p>
              </div>
            </form>
          ) : (
            <div className="ff-soft-in flex flex-col gap-6">
              <p className="text-[11px] tracking-wide text-black/50">YOUR ENQUIRY, READY TO SHARE</p>
              <pre className="whitespace-pre-wrap break-words ff-sans text-[14px] leading-relaxed border-t border-b border-black/15 py-6">{summary}</pre>
              <div className="flex flex-wrap items-center gap-4">
                <button type="button" onClick={copy} className="bg-[#111111] text-white text-[13px] px-6 py-3 rounded-md whitespace-nowrap cursor-pointer hover:bg-black/80 transition-colors">Copy enquiry</button>
                {mailto && <a href={mailto} className="text-[13px] border-b border-black/30 hover:border-black pb-0.5 whitespace-nowrap cursor-pointer">Open email ↗</a>}
                <button type="button" onClick={() => { setSummary(''); setCopied(''); }} className="text-[13px] text-black/55 hover:text-black whitespace-nowrap cursor-pointer">Edit details</button>
              </div>
              <p aria-live="polite" className="ff-serif italic text-[13px] text-black/55 min-h-[1.2em]">{copied}</p>
              <p className="text-[12px] text-black/45 max-w-[440px]">This is a demo template: the enquiry stays in your browser. Paste it into an email or message to the studio.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}